import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'

const STATUS_CONFIG = {
  open:        { label: 'Open',        bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  mitigating:  { label: 'Mitigating',  bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  closed:      { label: 'Closed',      bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  archived:    { label: 'Archived',    bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
}

const CATEGORY_OPTIONS = ['AML', 'CTF', 'PF', 'RG', 'Fraud', 'Privacy', 'Other']

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      {cfg.label}
    </span>
  )
}

function RiskScore({ likelihood, impact, residual }) {
  const inherent = (Number(likelihood) || 0) * (Number(impact) || 0)
  const colorFor = (s) => s >= 16 ? '#991b1b' : s >= 9 ? '#92400e' : s >= 4 ? '#1e40af' : '#166534'
  return (
    <div className="flex items-center gap-1.5 text-xs font-mono">
      <span title="Inherent = likelihood × impact">
        <span className="text-gray-500">I:</span>{' '}
        <span style={{ color: colorFor(inherent) }} className="font-semibold">{inherent}</span>
      </span>
      <span className="text-gray-300">·</span>
      <span title="Residual — after controls">
        <span className="text-gray-500">R:</span>{' '}
        <span style={{ color: residual != null ? colorFor(residual) : '#9ca3af' }} className="font-semibold">
          {residual != null ? residual : '—'}
        </span>
      </span>
    </div>
  )
}

function formatDate(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })
}

function StatCard({ label, value, color = '#0A5169', bg = '#e8f2f6' }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 min-w-[130px]">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-0.5" style={{ color: value > 0 ? color : '#9ca3af' }}>
        {value ?? '—'}
      </div>
      <div className="w-full h-1 mt-2 rounded-full" style={{ backgroundColor: bg }} />
    </div>
  )
}

// ─── Create modal ────────────────────────────────────────────────────────

function CreateRiskItemModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    typology_ref: '',
    category: '',
    likelihood: 3,
    impact: 3,
    residual_score: '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (open) {
      setForm({ title: '', description: '', typology_ref: '', category: '', likelihood: 3, impact: 3, residual_score: '' })
      setErr(null)
    }
  }, [open])

  const submit = useCallback(async () => {
    if (saving) return
    if (!form.title.trim()) {
      setErr('Title is required')
      return
    }
    setSaving(true)
    setErr(null)
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        typology_ref: form.typology_ref.trim() || undefined,
        category: form.category || undefined,
        likelihood: Number(form.likelihood),
        impact: Number(form.impact),
      }
      if (form.residual_score !== '' && !Number.isNaN(Number(form.residual_score))) {
        body.residual_score = Number(form.residual_score)
      }
      const res = await authFetch(getLocalApiUrl('/api/risk-items'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onCreated(json.data)
    } catch (e) {
      setErr(e.message || 'Failed to create risk item')
    } finally {
      setSaving(false)
    }
  }, [saving, form, onCreated])

  if (!open) return null
  const inherent = Number(form.likelihood || 0) * Number(form.impact || 0)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl border border-gray-100 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">New risk item</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <label htmlFor="ri-title" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="ri-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 255) }))}
              placeholder="e.g. Structuring cash-out to evade the TTR threshold"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="ri-desc" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Description
            </label>
            <textarea
              id="ri-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 8000) }))}
              placeholder="What specifically is the risk? Where does it manifest? Why is it a risk for this venue?"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ri-cat" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</label>
              <select
                id="ri-cat"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none focus:border-gray-300"
              >
                <option value="">—</option>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ri-typ" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Typology ref
              </label>
              <input
                id="ri-typ"
                value={form.typology_ref}
                onChange={(e) => setForm((f) => ({ ...f, typology_ref: e.target.value.slice(0, 64) }))}
                placeholder="structuring, pep-linked, third-party-collection…"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="ri-lik" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Likelihood (1–5)
              </label>
              <select
                id="ri-lik"
                value={form.likelihood}
                onChange={(e) => setForm((f) => ({ ...f, likelihood: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
              >
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ri-imp" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Impact (1–5)
              </label>
              <select
                id="ri-imp"
                value={form.impact}
                onChange={(e) => setForm((f) => ({ ...f, impact: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
              >
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ri-res" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Residual (0–25)
              </label>
              <input
                id="ri-res"
                type="number"
                min={0}
                max={25}
                value={form.residual_score}
                onChange={(e) => setForm((f) => ({ ...f, residual_score: e.target.value }))}
                placeholder="after controls"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
              />
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Inherent score = likelihood × impact ={' '}
            <span className="font-mono font-semibold text-gray-700">{inherent}</span>{' '}
            (computed automatically). Residual is the score you expect after current controls are applied.
          </div>
          {err && (
            <div className="p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            type="button"
            size="sm"
            className="bg-psg-teal hover:bg-psg-teal-700"
            disabled={saving || !form.title.trim()}
            onClick={submit}
          >
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />
            {saving ? 'Creating…' : 'Create risk item'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ───────────────────────────────────────────────────────────

export default function RiskRegister() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({ status: '', q: '', typology: '' })
  const [createOpen, setCreateOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [deleteErr, setDeleteErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const p = new URLSearchParams()
      if (filters.status) p.set('status', filters.status)
      if (filters.typology) p.set('typology', filters.typology)
      const [listRes, statsRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/risk-items${p.toString() ? `?${p}` : ''}`)),
        authFetch(getLocalApiUrl('/api/risk-items/stats')),
      ])
      const listJson = await listRes.json().catch(() => ({}))
      const statsJson = await statsRes.json().catch(() => ({}))
      if (!listJson.success) throw new Error(listJson.error || 'Failed to load risk items')
      setRows(listJson.data || [])
      if (statsJson.success) setStats(statsJson.data)
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [filters.status, filters.typology])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      (r.id && r.id.toLowerCase().includes(q)) ||
      (r.title && r.title.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q)) ||
      (r.typology_ref && r.typology_ref.toLowerCase().includes(q))
    )
  }, [rows, filters.q])

  const typologies = useMemo(() => {
    const s = new Set()
    for (const r of rows) if (r.typology_ref) s.add(r.typology_ref)
    return Array.from(s).sort()
  }, [rows])

  const exportCsv = () => {
    const headers = ['ID', 'Title', 'Status', 'Category', 'Typology', 'Likelihood', 'Impact', 'Inherent', 'Residual', 'Owner', 'Next review']
    const data = filtered.map((r) => [
      r.id, r.title, r.status, r.category || '', r.typology_ref || '',
      r.likelihood, r.impact, r.inherent_score, r.residual_score ?? '',
      r.owner_name || r.owner_id || '', r.next_review_at || '',
    ])
    const csv = [headers.join(','), ...data.map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `risk-register-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDelete = useCallback(async (item, e) => {
    e.stopPropagation()
    if (!item?.id || deletingId) return
    const ok = window.confirm(
      `Permanently delete ${item.id} — ${item.title}?\n\nThis cannot be undone. Linked controls will be removed with it.`
    )
    if (!ok) return
    setDeletingId(item.id)
    setDeleteErr(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}/permanent`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      await load()
    } catch (err) {
      setDeleteErr(err.message || 'Failed to delete risk item')
    } finally {
      setDeletingId(null)
    }
  }, [deletingId, load])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-psg-teal flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-6 h-6" />
            Risk Register
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Structured list of the risks the entity has identified. Each risk links to the rules, CDD triggers, policies, and training that address it — so an assessor can thread any control back to the risk it's there to mitigate.
          </p>
        </div>
        <Button type="button" className="bg-psg-teal hover:bg-psg-teal-700" size="sm" onClick={() => setCreateOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-1.5" /> New risk item
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <StatCard label="Total"       value={rows.length} />
        <StatCard label="Open"        value={stats?.by_status?.open || 0}        color="#92400e" bg="#fef3c7" />
        <StatCard label="Mitigating"  value={stats?.by_status?.mitigating || 0}  color="#1e40af" bg="#dbeafe" />
        <StatCard label="Closed"      value={stats?.by_status?.closed || 0}      color="#166534" bg="#dcfce7" />
        <StatCard label="Archived"    value={stats?.by_status?.archived || 0}    color="#475569" bg="#f1f5f9" />
        <StatCard label="Overdue reviews" value={stats?.overdue_reviews || 0}    color="#991b1b" bg="#fee2e2" />
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px] max-w-sm relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            placeholder="Search title / description / id…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
        <select
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
          value={filters.typology}
          onChange={(e) => setFilters((f) => ({ ...f, typology: e.target.value }))}
        >
          <option value="">All typologies</option>
          {typologies.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
          <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {deleteErr && (
        <div className="p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{deleteErr}</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Title</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Category</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Typology</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Controls</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Score</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Owner</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Next review</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">Loading…</td></tr>)}
              {error && !loading && (<tr><td colSpan={10} className="px-4 py-12 text-center text-red-600">{error}</td></tr>)}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                  No risk items yet. Create the first one to start linking controls back to documented risks.
                </td></tr>
              )}
              {!loading && !error && filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                  onClick={() => navigate(`/governance-safe/risk-register/${encodeURIComponent(r.id)}`)}
                >
                  <td className="px-4 py-3 font-mono font-medium text-psg-teal">{r.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{r.title}</div>
                    {r.description && (
                      <div className="text-xs text-gray-500 truncate max-w-md">{r.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.category || '—'}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{r.typology_ref || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex min-w-[1.5rem] justify-center px-2 py-0.5 rounded-full text-xs font-semibold ${Number(r.control_count) > 0 ? 'bg-psg-teal/10 text-psg-teal' : 'bg-gray-100 text-gray-400'}`}>
                      {Number(r.control_count) || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3"><RiskScore likelihood={r.likelihood} impact={r.impact} residual={r.residual_score} /></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.owner_name || r.owner_id || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(r.next_review_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      title={`Delete ${r.id}`}
                      aria-label={`Delete ${r.id}`}
                      className="inline-flex p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                      disabled={deletingId === r.id}
                      onClick={(e) => handleDelete(r, e)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400 inline-block align-middle ml-1" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <CreateRiskItemModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(item) => {
          setCreateOpen(false)
          if (item?.id) navigate(`/governance-safe/risk-register/${encodeURIComponent(item.id)}`)
          else load()
        }}
      />
    </div>
  )
}
